import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Avatar,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Chip,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Edit as EditIcon,
  PhotoCamera as CameraIcon,
  Save as SaveIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { selectUser } from '../../store/slices/authSlice';
import { belarusRegions, regionsList, getCitiesByRegion } from '../../data/belarusRegions';
import DeleteAccountSection from '../../components/DeleteAccountSection';

// Mock categories/interests data
const categories = [
  'Technology',
  'Fashion',
  'Food & Dining',
  'Travel',
  'Health & Fitness',
  'Entertainment',
  'Education',
  'Finance',
  'Automotive',
  'Home & Garden'
];

export default function ClientProfile() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [editMode, setEditMode] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  
  const user = useSelector(selectUser);
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  
  // Helper function to get full avatar URL
  const getAvatarUrl = (avatarPath) => {
    if (!avatarPath) return null;
    if (avatarPath.startsWith('http')) return avatarPath;
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    const baseUrl = API_BASE.replace('/api', '');
    return `${baseUrl}${avatarPath}`;
  };
  
  // User data state
  // Simplified client profile: only basic contact/branding fields
  const [userData, setUserData] = useState({
    fullName: user?.fullName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    avatar: user?.avatar || null,
    companyName: user?.companyName || ''
  });

  // Load fresh profile on mount to keep avatar and data in sync
  const loadProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(`${API_BASE}/users/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUserData({
          fullName: data.fullName || '',
          email: data.email || '',
          phone: data.phone || '',
          avatar: data.avatar || null,
          companyName: data.companyName || ''
        });
        const stored = JSON.parse(localStorage.getItem('user') || '{}');
        
        // Remove undefined/null values from data to prevent overwriting stored values
        const cleanData = Object.fromEntries(
          Object.entries(data).filter(([_, v]) => v !== undefined && v !== null)
        );
        
        // Start with stored data, then update with fresh data from server
        // This ensures critical fields (id, role) are never lost
        const updatedUser = { 
          ...stored,
          ...cleanData,
          // Explicitly ensure these critical fields are always present
          id: stored.id,
          role: stored.role
        };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        // Notify AppBar to reload user data
        window.dispatchEvent(new Event('authChange'));
      }
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      // Мгновенное превью
      setProfileImage(URL.createObjectURL(file));
      const token = localStorage.getItem('token');
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = storedUser?.id;
      const formData = new FormData();
      formData.append('avatar', file);
      try {
        // 1. Загружаем файл
        const res = await fetch(`${API_BASE}/upload/avatar`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        });
        if (res.ok) {
          const data = await res.json();
          // 2. Обновляем профиль пользователя
          const updateRes = await fetch(`${API_BASE}/users/me`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({ avatar: data.avatarUrl })
          });
          if (updateRes.ok) {
            // Обновляем userData с новым аватаром
            setUserData((prev) => ({ ...prev, avatar: data.avatarUrl }));
            // Обнуляем превью, чтобы использовался путь из userData через getAvatarUrl
            setProfileImage(null);
            // Обновляем localStorage
            const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
            storedUser.avatar = data.avatarUrl;
            localStorage.setItem('user', JSON.stringify(storedUser));
            // Notify AppBar
            window.dispatchEvent(new Event('authChange'));
          } else {
            const err = await updateRes.json().catch(() => ({}));
            alert(err.error || 'Ошибка обновления профиля');
          }
        } else {
          const err = await res.json().catch(() => ({}));
          alert(err.error || 'Ошибка загрузки фото');
        }
      } catch (e) {
        alert('Ошибка загрузки фото');
      }
    }
  };

  const handleDeleteAvatar = async () => {
    if (!window.confirm('Вы уверены, что хотите удалить фото профиля?')) return;
    
  const token = localStorage.getItem('token');
  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const userId = storedUser?.id;
    
    try {
      const response = await fetch(`${API_BASE}/users/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ avatar: null })
      });
      
      if (response.ok) {
        // Обновляем userData
        setUserData((prev) => ({ ...prev, avatar: null }));
        // Обнуляем превью
        setProfileImage(null);
        // Обновляем localStorage
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        storedUser.avatar = null;
        localStorage.setItem('user', JSON.stringify(storedUser));
        // Notify AppBar
        window.dispatchEvent(new Event('authChange'));
      } else {
        const err = await response.json().catch(() => ({}));
        alert(err.error || 'Ошибка удаления фото');
      }
    } catch (error) {
      console.error('Error deleting avatar:', error);
      alert('Ошибка удаления фото');
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const userId = storedUser?.id;
      
      // Only save basic fields for client profiles
      const resp = await fetch(`${API_BASE}/users/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          fullName: userData.fullName,
          companyName: userData.companyName
        })
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || 'Не удалось сохранить профиль');
      }
      
      // Update localStorage preserving role and other fields
      const updatedUser = {
        ...storedUser,
        fullName: userData.fullName,
        companyName: userData.companyName
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      // Trigger auth change event to update header
      window.dispatchEvent(new Event('authChange'));
      
      setSaveSuccess(true);
      setEditMode(false);
      
      // Reload current profile data without navigation
      setTimeout(() => {
        loadProfile();
      }, 1000);
    } catch (error) {
      console.error('Error saving profile:', error);
      alert(error.message || 'Failed to save profile');
    }
  };

  // Interests & demographics removed for client accounts

  return (
    <Box>
      <Paper 
        elevation={3} 
        sx={{ 
          p: { xs: 2, md: 3 },
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: { xs: '16px', md: '32px' },
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}
      >
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1.5, sm: 0 },
          mb: 3,
        }}>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 700, 
              color: '#2d3748',
              fontSize: { xs: '1.25rem', md: '1.5rem' }
            }}
          >
            Информация профиля
          </Typography>
          <Box sx={{
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
            width: { xs: '100%', sm: 'auto' },
            '& .MuiButton-root': { width: { xs: '100%', sm: 'auto' } },
          }}>
            <Button
              variant={editMode ? 'contained' : 'outlined'}
              startIcon={editMode ? <SaveIcon /> : <EditIcon />}
              onClick={editMode ? handleSave : () => setEditMode(true)}
              sx={editMode ? {
                background: 'linear-gradient(135deg, #56ab2f 0%, #2d7a1f 100%)',
                color: '#fff',
                fontWeight: 600,
                borderRadius: '12px',
                textTransform: 'none',
                px: 2.5,
                boxShadow: '0 4px 14px rgba(45, 122, 31, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #2d7a1f 0%, #56ab2f 100%)',
                  boxShadow: '0 6px 20px rgba(45, 122, 31, 0.6)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              } : {
                borderColor: '#0984e3',
                color: '#0984e3',
                fontWeight: 600,
                borderRadius: '12px',
                textTransform: 'none',
                px: 2.5,
                borderWidth: '2px',
                '&:hover': {
                  borderColor: '#0984e3',
                  backgroundColor: 'rgba(9, 132, 227, 0.08)',
                  borderWidth: '2px',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              {editMode ? 'Сохранить' : 'Редактировать'}
            </Button>
          </Box>
        </Box>

        <Grid container spacing={4}>
          {/* Profile Image Section */}
          <Grid item xs={12} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Box 
                sx={{ 
                  position: 'relative',
                  display: 'inline-block',
                  '&:hover .delete-overlay': {
                    opacity: editMode && userData.avatar ? 1 : 0
                  }
                }}
              >
                <Avatar
                  src={profileImage || getAvatarUrl(userData.avatar)}
                  sx={{ 
                    width: 150, 
                    height: 150, 
                    mb: 2,
                    border: '4px solid rgba(116, 185, 255, 0.3)',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)'
                  }}
                />
                {editMode && userData.avatar && (
                  <Box
                    className="delete-overlay"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      mb: 2,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, rgba(255, 118, 117, 0.9) 0%, rgba(214, 48, 49, 0.9) 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: 0,
                      transition: 'opacity 0.3s ease',
                      cursor: 'pointer'
                    }}
                    onClick={handleDeleteAvatar}
                  >
                    <DeleteIcon sx={{ fontSize: 40, color: 'white' }} />
                  </Box>
                )}
              </Box>
              {editMode && (
                <Box>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="icon-button-file"
                    type="file"
                    onChange={handleImageUpload}
                  />
                  <label htmlFor="icon-button-file">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<CameraIcon />}
                      sx={{
                        borderColor: '#0984e3',
                        color: '#0984e3',
                        fontWeight: 600,
                        borderRadius: '12px',
                        textTransform: 'none',
                        borderWidth: '2px',
                        '&:hover': {
                          borderColor: '#0984e3',
                          backgroundColor: 'rgba(9, 132, 227, 0.08)',
                          borderWidth: '2px'
                        }
                      }}
                    >
                      Загрузить фото
                    </Button>
                  </label>
                </Box>
              )}
            </Box>
          </Grid>

          {/* Basic Information */}
          <Grid item xs={12} md={9}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Полное имя"
                  value={userData.fullName}
                  disabled={!editMode}
                  onChange={(e) => setUserData({ ...userData, fullName: e.target.value })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)'
                      },
                      '&:hover fieldset': {
                        borderColor: editMode ? '#0984e3' : 'rgba(0, 0, 0, 0.23)'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#0984e3',
                        borderWidth: '2px'
                      },
                      '&.Mui-disabled': {
                        backgroundColor: 'rgba(0, 0, 0, 0.02)'
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Название компании"
                  value={userData.companyName}
                  disabled={!editMode}
                  onChange={(e) => setUserData({ ...userData, companyName: e.target.value })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)'
                      },
                      '&:hover fieldset': {
                        borderColor: editMode ? '#0984e3' : 'rgba(0, 0, 0, 0.23)'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#0984e3',
                        borderWidth: '2px'
                      },
                      '&.Mui-disabled': {
                        backgroundColor: 'rgba(0, 0, 0, 0.02)'
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  value={userData.email}
                  disabled
                  helperText="Email нельзя изменить"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      '&.Mui-disabled': {
                        backgroundColor: 'rgba(0, 0, 0, 0.02)'
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Телефон"
                  value={userData.phone}
                  disabled
                  helperText="Телефон нельзя изменить"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      '&.Mui-disabled': {
                        backgroundColor: 'rgba(0, 0, 0, 0.02)'
                      }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Demographics removed for client profile */}

          {/* Interests removed for client profile */}
        </Grid>
      </Paper>

      {/* Account deletion (customer). Client profile has no password block, so it goes right below the profile card */}
      <DeleteAccountSection sx={{ mx: 0 }} />

      <Snackbar
        open={saveSuccess}
        autoHideDuration={6000}
        onClose={() => setSaveSuccess(false)}
      >
        <Alert 
          onClose={() => setSaveSuccess(false)} 
          severity="success"
          sx={{ width: '100%' }}
        >
          Профиль успешно обновлён!
        </Alert>
      </Snackbar>
    </Box>
  );
}